/**
 * Dev-only settings dialog (opened from the safe-zone preview controls gear).
 *
 * Lets developers switch the platform experience override, flip account-level
 * cohort flags, and set runtime Remote Config overrides without editing .env
 * files or hand-crafting query params / storage entries. The override
 * mechanisms live in @um/ui:
 * - platformExperience: sessionStorage override behind getPlatformExperience()
 * - setDevConfigOverride: DEV-only localStorage layer read first by
 *   getRemoteConfigValue()
 *
 * Most consumers read these values once (e.g. at flow start), so the dialog
 * offers a reload button to apply changes everywhere.
 */

import { useState } from "react";
import {
  getDevConfigOverrides,
  setDevConfigOverride,
  getEnv,
  getPlatformExperience,
  getPlatformExperienceOverride,
  getRemoteConfigValue,
  getRemoteConfigValueSource,
  setPlatformExperience,
  useAuthStore,
  type PlatformExperience,
} from "@um/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import config from "@/config";

const REMOTE_CONFIG_KEYS = Object.keys(config.remoteConfig ?? {});

interface DevSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DevSettingsDialog({
  open,
  onOpenChange,
}: DevSettingsDialogProps) {
  // Values live in localStorage; bump to re-read them after each change.
  const [, setRevision] = useState(0);
  const bump = () => setRevision((n) => n + 1);

  const experienceOverride = getPlatformExperienceOverride();
  const defaultExperience =
    (getEnv("PLATFORM_EXPERIENCE") as PlatformExperience | undefined) || "web";
  const overrides = getDevConfigOverrides();

  const selectedTestGroupId = useAuthStore((s) => s.selectedTestGroupId);
  const setSelectedTestGroupId = useAuthStore((s) => s.setSelectedTestGroupId);

  // Dental pilot cohort. Unlike the overrides above this is a real account
  // setting written to Firestore — the same field the pilot's entry point
  // will stamp — so toggling it here reproduces a participant exactly.
  const dentalPilot = useAuthStore((s) => s.accountSettings?.dentalPilot === true);
  const updateAccountSettings = useAuthStore((s) => s.updateAccountSettings);
  const [savingDentalPilot, setSavingDentalPilot] = useState(false);
  const [dentalPilotError, setDentalPilotError] = useState<string | null>(null);

  const setDentalPilot = async (next: boolean) => {
    setSavingDentalPilot(true);
    setDentalPilotError(null);
    try {
      await updateAccountSettings({ dentalPilot: next });
      // updateAccountSettings DROPS the write (console warning only) when
      // there is no accountId yet, and the checkbox is controlled by store
      // state — so without this check a dropped write just looks like a
      // checkbox that refuses to tick. Confirm the store actually took it.
      if (useAuthStore.getState().accountSettings?.dentalPilot !== next) {
        setDentalPilotError(
          "Didn't save — no account loaded yet. Finish signing in, then try again."
        );
      }
    } catch (err) {
      setDentalPilotError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingDentalPilot(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[2147483647] max-w-md">
        <DialogHeader>
          <DialogTitle>Dev settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 text-sm">
          <section className="space-y-1.5">
            <label
              htmlFor="dev-platform-experience"
              className="block font-semibold text-gray-800"
            >
              Platform experience
            </label>
            <select
              id="dev-platform-experience"
              value={experienceOverride ?? "default"}
              onChange={(e) => {
                const value = e.target.value;
                setPlatformExperience(
                  value === "default" ? null : (value as PlatformExperience)
                );
                bump();
              }}
              className="w-full cursor-pointer rounded border border-gray-200 bg-white px-2 py-1.5 text-gray-900"
            >
              <option value="default">Default ({defaultExperience})</option>
              <option value="app">app</option>
              <option value="web">web</option>
            </select>
            <p className="text-xs text-gray-500">
              Resolves to{" "}
              <span className="font-mono font-semibold">
                {getPlatformExperience()}
              </span>
              . Also settable via <span className="font-mono">?experience=</span>{" "}
              or <span className="font-mono">VITE_PLATFORM_EXPERIENCE</span>.
            </p>
          </section>

          <section className="space-y-1.5">
            <label
              htmlFor="dev-test-group"
              className="block font-semibold text-gray-800"
            >
              Test resource group
            </label>
            <input
              id="dev-test-group"
              type="text"
              value={selectedTestGroupId ?? ""}
              placeholder="No test group"
              onChange={(e) =>
                setSelectedTestGroupId(e.target.value.trim() || null)
              }
              className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs text-gray-900 placeholder:font-sans placeholder:text-gray-400"
            />
            <p className="text-xs text-gray-500">
              Enables the "New test user" sign-in option and tags created
              accounts. Also settable via <span className="font-mono">?test=</span>.
            </p>
          </section>

          <section className="space-y-1.5">
            <label
              htmlFor="dev-dental-pilot"
              className="flex items-center gap-2 font-semibold text-gray-800"
            >
              <input
                id="dev-dental-pilot"
                type="checkbox"
                checked={dentalPilot}
                disabled={savingDentalPilot}
                onChange={(e) => void setDentalPilot(e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-teal-600 disabled:opacity-50"
              />
              Dental pilot participant
            </label>
            <p className="text-xs text-gray-500">
              Writes{" "}
              <span className="font-mono">dentalPilot</span> to this account's
              settings — the dental support section and Home framing read it.
              Persists across devices; turn it off to see the standard
              experience again.
            </p>
            {dentalPilotError && (
              <p className="text-xs font-medium text-red-600">{dentalPilotError}</p>
            )}
          </section>

          <section className="space-y-3">
            <p className="font-semibold text-gray-800">
              Remote Config overrides
            </p>
            {REMOTE_CONFIG_KEYS.map((key) => {
              const effective = getRemoteConfigValue(key);
              const source = getRemoteConfigValueSource(key);
              return (
                <div key={key} className="space-y-1">
                  <label
                    htmlFor={`dev-config-${key}`}
                    className="flex items-baseline justify-between gap-2"
                  >
                    <span className="font-mono text-xs text-gray-800">
                      {key}
                    </span>
                    <span className="text-xs text-gray-500">
                      ={" "}
                      <span className="font-mono">
                        {effective === undefined ? "(unset)" : String(effective)}
                      </span>{" "}
                      <span className="rounded bg-gray-100 px-1 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                        {source}
                      </span>
                    </span>
                  </label>
                  <input
                    id={`dev-config-${key}`}
                    type="text"
                    value={overrides[key] ?? ""}
                    placeholder="No override — using value above"
                    onChange={(e) => {
                      setDevConfigOverride(key, e.target.value);
                      bump();
                    }}
                    className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs text-gray-900 placeholder:font-sans placeholder:text-gray-400"
                  />
                </div>
              );
            })}
            <p className="text-xs text-gray-500">
              Overrides are dev-only and stored in localStorage. Most values are
              read once at startup or flow start — reload to apply.
            </p>
          </section>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full rounded-lg bg-gray-800 py-2 font-semibold text-white transition-colors hover:bg-gray-700"
          >
            Reload app to apply
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
